import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@reach/tabs'
import { clsx } from 'clsx'
import * as React from 'react'
import {
	data as json,
	type HeadersFunction,
	type MetaFunction,
	Link,
	Outlet,
	useMatches,
	useNavigate,
} from 'react-router'
import { ButtonLink } from '#app/components/button.tsx'
import { Grid } from '#app/components/grid.tsx'
import { ChevronDownIcon, ChevronUpIcon } from '#app/components/icons.tsx'
import { PodcastSubs } from '#app/components/podcast-subs.tsx'
import { BlogSection } from '#app/components/sections/blog-section.tsx'
import { FeaturedSection } from '#app/components/sections/featured-section.tsx'
import { HeroSection } from '#app/components/sections/hero-section.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { H4, H6, Paragraph } from '#app/components/typography.tsx'
import { externalLinks } from '#app/external-links.tsx'
import {
	getImageBuilder,
	getImgProps,
	images,
} from '#app/images.tsx'
import { type RootLoaderType } from '#app/root.tsx'
import { getBlogRecommendations } from '#app/utils/blog.server.ts'
import {
	getCWKEpisodePath,
	getFeaturedEpisode,
} from '#app/utils/chats-with-kent.ts'
import {
	formatDate,
	formatDuration,
	listify,
	reuseUsefulLoaderHeaders,
} from '#app/utils/misc.ts'
import { ChatsEpisodeUIStateProvider } from '#app/utils/providers.tsx'
import { getSeasonListItems } from '#app/utils/simplecast.server.ts'
import { getServerTimeHeader } from '#app/utils/timing.server.ts'
import { type Route } from './+types/_layout'

export async function loader({ request }: Route.LoaderArgs) {
	const timings = {}
	const blogRecommendations = await getBlogRecommendations({ request, timings })
	const seasons = (await getSeasonListItems({ request })).reverse()
	const episodeCount = seasons.reduce(
		(acc, season) => acc + season.episodes.length,
		0,
	)
	const socialMetas = (
		await import('#app/og/page-meta.server.ts')
	).buildPageSocialMetasForRequest(request, {
		title: 'Chats with Kent C. Dodds Podcast',
		description: `Become a better person with ${episodeCount} interesting and actionable conversations with interesting people.`,
		keywords: 'chats with kent, kent c. dodds',
		socialImage: {
			kind: 'generic-social',
			words: 'Listen to the Chats with Kent Podcast',
			featuredImage: images.kayak.id,
		},
	})

	return json(
		{
			seasons,
			blogRecommendations,
			socialMetas,
		},
		{
			headers: {
				'Cache-Control': 'private, max-age=3600',
				Vary: 'Cookie',
				'Server-Timing': getServerTimeHeader(timings),
			},
		},
	)
}

export const headers: HeadersFunction = reuseUsefulLoaderHeaders

export const meta: MetaFunction<typeof loader> = ({ data }) =>
	data?.socialMetas ?? [{ title: 'Chats with Kent Seasons not found' }]

function PodcastHome({ loaderData: data }: Route.ComponentProps) {
	const [sortOrder, setSortOrder] = React.useState<'desc' | 'asc'>('asc')
	const navigate = useNavigate()
	const matches = useMatches()
	const last = matches[matches.length - 1]

	const seasonNumber = last?.params.season
		? Number(last.params.season)
		: // we use the first one because the seasons are in reverse order
			// oh, and this should never happen anyway because we redirect
			// in the event there's no season param. But it's just to be safe.
			(data.seasons[0]?.seasonNumber ?? 1)

	const currentSeason = data.seasons.find(
		(s) => s.seasonNumber === seasonNumber,
	)
	const tabIndex = currentSeason ? data.seasons.indexOf(currentSeason) : 0

	function handleTabChange(index: number) {
		const chosenSeason = data.seasons[index]
		if (chosenSeason) {
			void navigate(String(chosenSeason.seasonNumber).padStart(2, '0'), {
				preventScrollReset: true,
			})
		}
	}

	const allEpisodes = data.seasons.flatMap((s) => s.episodes)
	const featured = getFeaturedEpisode(allEpisodes)

	return (
		<>
			<HeroSection
				title="Listen to chats with Kent C. Dodds here."
				subtitle="Find all episodes of my podcast below."
				imageBuilder={images.kayak}
				imageSize="large"
			/>

			<Grid>
				<H6 as="div" className="col-span-full mb-6">
					Listen to the podcasts here
				</H6>

				<PodcastSubs
					apple={externalLinks.applePodcast}
					pocketCasts={externalLinks.pocketCasts}
					spotify={externalLinks.spotify}
					rss={externalLinks.simpleCast}
				/>
			</Grid>

			{featured ? (
				<>
					<Spacer size="xs" />
					<FeaturedSection
						cta="Listen to this episode"
						caption="Featured episode"
						subTitle={`Season ${featured.seasonNumber} Episode ${
							featured.episodeNumber
						} — ${formatDate(featured.publishedAt)} — ${formatDuration(
							featured.duration,
						)}`}
						title={featured.title}
						href={getCWKEpisodePath(featured)}
						imageUrl={featured.image}
						imageAlt={listify(featured.guests.map((g) => g.name))}
					/>
				</>
			) : null}

			<Spacer size="base" />

			<Grid>
				<div className="col-span-full lg:col-span-6">
					<img
						title="Photo by Jukka Aalho / Kertojan ääni: https://kertojanaani.fi"
						{...getImgProps(
							getImageBuilder(
								'unsplash/photo-1590602847861-f357a9332bbc',
								'A SM7B Microphone',
							),
							{
								className: 'rounded-lg object-cover',
								widths: [512, 650, 840, 1024, 1300, 1680, 2000, 2520],
								sizes: [
									'(max-width: 1023px) 80vw',
									'(min-width: 1024px) and (max-width: 1620px) 40vw',
									'630px',
								],
								transformations: {
									fit: 'cover',
									aspectRatio: '3:4',
								},
							},
						)}
					/>
				</div>
				<Spacer size="xs" className="col-span-full block lg:hidden" />
				<div className="col-span-full lg:col-span-5 lg:col-start-8">
					<H4 as="p">{`What's this all about?`}</H4>
					<div className="flex flex-col gap-3">
						<Paragraph>
							{`The goal of the Chats with Kent Podcast is to `}
							<strong>help you become a better person.</strong>
							{`
                With each episode, there's a key takeaway and a specific action
                item to help you on your path to becoming the best person you
                can be.
              `}
						</Paragraph>
						<Paragraph>
							{`
                Before each show, I ask the guest to share with me the change
                they would like to see in the world. Any change at all. Whether
                it's related to software development or not. And then we
                brainstorm a specific thing we can invite you to do at the end
                of the show to help push that change in the world along.
                Something small, but meaningful.
              `}
						</Paragraph>
						<Paragraph>
							{`
                Once we know what we want to commit you to, I kick things off
                and try to steer the conversation in a direction that will
                prepare you to accept that invitation and hopefully help you
                make that change in your life. I hope you take advantage of this
                opportunity.
              `}
						</Paragraph>
						<Paragraph>{`Enjoy the show.`}</Paragraph>
					</div>
				</div>
			</Grid>

			<Spacer size="base" />

			<Tabs
				as={Grid}
				className="mb-24 lg:mb-64"
				index={tabIndex}
				onChange={handleTabChange}
			>
				<TabList className="col-span-full mb-20 flex flex-col items-start bg-transparent lg:flex-row lg:flex-nowrap lg:gap-x-12 lg:overflow-x-auto lg:overscroll-x-contain lg:pb-1">
					{data.seasons.map((season) => (
						<Tab
							key={season.seasonNumber}
							// Because we have a link right under the tab, we'll keep this off
							// the tab "tree" and rely on focusing/activating the link.
							tabIndex={-1}
							className="border-none p-0 text-left text-4xl leading-tight focus:bg-transparent focus:outline-none lg:shrink-0"
						>
							{/*
                The link is here for progressive enhancement. Even though this
                is a tab, it's actually navigating to a route, so semantically
                it should be a link. By making it a link, it'll work with JS
                off, but more importantly it'll allow people to meta-click it.
              */}
							<Link
								preventScrollReset
								className={clsx(
									'hover:text-primary focus:text-primary whitespace-nowrap focus:outline-none',
									{
										'text-primary': season.seasonNumber === seasonNumber,
										'text-slate-500': season.seasonNumber !== seasonNumber,
									},
								)}
								to={String(season.seasonNumber).padStart(2, '0')}
								onClick={(e) => {
									if (e.metaKey) {
										e.stopPropagation()
									} else {
										e.preventDefault()
									}
								}}
							>
								{`Season ${season.seasonNumber}`}
							</Link>
						</Tab>
					))}
				</TabList>

				{currentSeason ? (
					<div className="col-span-full mb-6 flex flex-col lg:mb-12 lg:flex-row lg:justify-between">
						<H6
							as="h2"
							className="col-span-full mb-10 flex flex-col lg:mb-0 lg:flex-row"
						>
							<span>Chats with Kent C. Dodds</span>
							&nbsp;
							<span>{`Season ${currentSeason.seasonNumber} — ${currentSeason.episodes.length} episodes`}</span>
						</H6>

						<button
							className="text-primary group relative text-lg font-medium focus:outline-none"
							onClick={() =>
								setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
							}
						>
							<div className="bg-secondary absolute -top-2 -right-4 -bottom-2 -left-4 rounded-lg opacity-0 transition group-hover:opacity-100 group-focus:opacity-100" />
							<span className="relative inline-flex items-center">
								{sortOrder === 'asc' ? (
									<>
										Showing oldest first
										<ChevronUpIcon className="ml-2 text-gray-400" />
									</>
								) : (
									<>
										Showing newest first
										<ChevronDownIcon className="ml-2 text-gray-400" />
									</>
								)}
							</span>
						</button>
					</div>
				) : null}

				<TabPanels className="col-span-full">
					{data.seasons.map((season) => (
						<TabPanel
							key={season.seasonNumber}
							className="border-t border-gray-200 focus:outline-none dark:border-gray-600"
						>
							<ChatsEpisodeUIStateProvider value={{ sortOrder }}>
								<Outlet />
							</ChatsEpisodeUIStateProvider>
						</TabPanel>
					))}
				</TabPanels>
			</Tabs>

			{data.seasons.length === 0 ? (
				<Grid className="mb-24">
					<div className="col-span-full rounded-lg border border-gray-200 p-8 dark:border-gray-600">
						<H4 as="h2" className="mb-3">
							No chats are available right now.
						</H4>
						<Paragraph className="mb-4">
							We are likely having trouble with our Simplecast integration.
							Please try again soon, or listen directly on the{' '}
							<a
								href={externalLinks.simpleCast}
								target="_blank"
								rel="noreferrer noopener"
								className="text-primary underline"
							>
								Simplecast feed
							</a>
							.
						</Paragraph>
						<ButtonLink variant="primary" to={externalLinks.simpleCast}>
							Open Simplecast feed
						</ButtonLink>
					</div>
				</Grid>
			) : null}

			<BlogSection
				articles={data.blogRecommendations}
				title="Looking for more content?"
				description="Have a look at these articles."
			/>
		</>
	)
}

export default PodcastHome
